import { useState } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { AlertTriangle, PauseCircle, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { planLabel, formatNumber } from "@/lib/plans";

const DISMISS_KEY = "il_free_banner_dismissed";

/**
 * One line above every app page when the account needs attention.
 * Most urgent first: paused account → failed payment → plan ending → Free plan.
 * Only the Free-plan nudge can be dismissed (for this browser session).
 */
export default function AccountBanner() {
  const { user, refreshUser } = useAuth();
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem(DISMISS_KEY) === "1"; } catch { return false; }
  });
  if (!user || user === false || !user.id) return null;

  const left = Math.max(0, (user.credits_total ?? 0) - (user.credits_used ?? 0));
  const plan = user.plan || "free";

  if (user.account_status === "frozen") {
    return (
      <Bar tone="danger" icon={PauseCircle} testid="banner-frozen">
        Your account is paused, so reveals and Enrich are off. Search still works. Email support@inboundlabs.app to sort it out.
      </Bar>
    );
  }

  if (user.subscription_status === "past_due") {
    return (
      <Bar tone="danger" icon={AlertTriangle} testid="banner-past-due">
        Your last payment didn't go through. <Link to="/billing" className="font-semibold underline underline-offset-2">Update your payment method</Link> to keep your plan.
      </Bar>
    );
  }

  if (plan !== "free" && user.cancel_at_period_end) {
    const resume = async () => {
      const { error } = await supabase.rpc("resume_subscription");
      if (error) { toast.error("Couldn't keep your plan. Please try again."); return; }
      await refreshUser();
      toast.success("Your plan will renew as normal.");
    };
    const end = user.current_period_end ? format(new Date(user.current_period_end), "d MMM yyyy") : "the end of this billing period";
    return (
      <Bar tone="warning" icon={AlertTriangle} testid="banner-ending">
        Your {planLabel(plan)} plan ends on {end}.{" "}
        <button onClick={resume} className="font-semibold underline underline-offset-2" data-testid="banner-keep-plan">Keep my plan</button>
      </Bar>
    );
  }

  if (plan === "free" && !dismissed) {
    const dismiss = () => {
      setDismissed(true);
      try { sessionStorage.setItem(DISMISS_KEY, "1"); } catch { /* noop */ }
    };
    return (
      <Bar tone="violet" icon={Sparkles} testid="banner-free" onDismiss={dismiss}>
        You're on the Free plan with {formatNumber(left)} {left === 1 ? "credit" : "credits"} left.{" "}
        <Link to="/choose-plan" className="font-semibold underline underline-offset-2" data-testid="banner-choose-plan">Get 5,000 credits a month, first month $4</Link>
      </Bar>
    );
  }
  return null;
}

function Bar({ tone, icon: Icon, children, testid, onDismiss }) {
  const cls = tone === "danger" ? "bg-danger-soft text-destructive"
    : tone === "warning" ? "bg-warning-soft text-warning" : "bg-violet-soft text-violet-text";
  return (
    <div role="status" data-testid={testid}
      className={`flex items-center gap-2 border-b border-border px-6 py-2 text-sm md:px-8 ${cls}`}>
      <Icon className="h-4 w-4 shrink-0" />
      <div className="min-w-0 flex-1">{children}</div>
      {onDismiss && (
        <button onClick={onDismiss} aria-label="Dismiss" className="rounded-full p-1 hover:bg-primary/10">
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}
